/**
 * Centralized form messages - single source of truth for form-related text.
 *
 * Contains all user-facing text used in form components throughout the app.
 * This includes placeholders, helper text, error messages, and labels.
 *
 * @example
 * import { formMessages } from '@/constants';
 * <TextField placeholder={formMessages.placeholders.search} />
 */

/**
 * Helper to pluralize entity names
 */
function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`;
}

export const formMessages = {
  /**
   * Common placeholder text
   */
  placeholders: {
    /** Generic search */
    search: 'Search...',

    /** Generic select */
    select: 'Select...',

    /** Date picker */
    selectDate: 'Select date',

    /** Time picker */
    selectTime: 'Select time',

    /** Click to edit inline field */
    clickToEdit: 'Click to edit',

    /** Search contacts, filters, labels */
    searchContactsFiltersLabels: 'Search contacts, filters, or labels...',

    /** Type to search */
    typeToSearch: 'Type to search...',
  },

  /**
   * Selection status messages
   */
  selection: {
    /** Selection count display */
    count: (selected: number, total: number) => `${selected}/${total} selected`,

    /** Items selected display */
    itemsSelected: (count: number, itemType: string) =>
      `${count} ${pluralize(itemType, count)} selected`,
  },

  /**
   * File upload messages
   */
  fileUpload: {
    /** Default drop zone text */
    dropzone: 'Drag and drop files here, or click to select',

    /** Max file size */
    maxSize: (size: string) => `Max size: ${size}`,

    /** Max files allowed */
    maxFilesAllowed: (count: number) => `Maximum ${count} files allowed`,

    /**
     * Accessible names for the per-file status glyph. The icon is the only
     * thing distinguishing an uploaded row from a failed one, so it is
     * labelled rather than left decorative.
     */
    statusUploaded: 'Uploaded',
    statusFailed: 'Upload failed',

    /** File too large error */
    fileTooLarge: (fileName: string, maxSize: string) =>
      `File "${fileName}" exceeds maximum size of ${maxSize}`,

    /** File type not accepted */
    fileTypeNotAccepted: (fileType: string) =>
      `File type "${fileType}" is not accepted`,

    /** Some files in a dropped/selected batch failed validation — the rest
     * are still added. `reasons` are the individual per-file messages above
     * (fileTooLarge / fileTypeNotAccepted), each already naming its file. */
    filesSkipped: (reasons: string[]) =>
      reasons.length === 1
        ? `1 file was skipped: ${reasons[0]}`
        : `${reasons.length} ${pluralize('file', reasons.length)} were skipped: ${reasons.join('; ')}`,

    /** Upload failed */
    uploadFailed: 'Upload failed',
  },

  /**
   * Unified file-upload copy (file-handling §14.3): client-side validation
   * rejections, and the FileRef state vocabulary `<FileAttachment>` renders.
   * Every surface that shows an uploaded file speaks this vocabulary, so a
   * processing or failed file never reads as "no file".
   */
  upload: {
    /** Extension rejected before upload — names the file and what is accepted. */
    extensionRejected: (fileName: string, accepted: string) =>
      `"${fileName}" isn't an accepted file type. Accepted: ${accepted}`,

    /**
     * Over the per-type cap. `typeLabel` is the plural noun for the file's
     * type so the cap reads as a rule, not an arbitrary number:
     * "This file is 3.2 GB — videos can be up to 2 GB".
     */
    tooLarge: (size: string, typeLabel: string, maxSize: string) =>
      `This file is ${size} — ${typeLabel} can be up to ${maxSize}`,

    /** Plural nouns used by `tooLarge`, keyed by FileRef type. */
    typeLabels: {
      image: 'images',
      video: 'videos',
      audio: 'audio files',
      document: 'documents',
      other: 'files',
    } as Record<string, string>,

    /** Filename + size chip: "photo.png (2.3 MB)". */
    fileWithSize: (fileName: string, size: string) => `${fileName} (${size})`,

    /** Suffix on a chip whose file is still being processed server-side. */
    processingSuffix: '(processing)',

    /** Overlay copy while processing; `duration` comes from fileRefProcessingCopy. */
    processing: (duration: string) => `Processing — this usually takes ${duration}`,

    /** Compact "we didn't accept that file" label for space-constrained cells. */
    rejected: 'Rejected',

    /** Terminal failure, generic. Deliberately blameless — the default reason. */
    failed: "We couldn't process this file",

    /**
     * Manager-facing failure reasons (file-handling plan A3, "manager sees"):
     * a reviewer must be able to tell "malware — object removed" from "the
     * upload never finished" from "we couldn't process it". Learner surfaces
     * never show these (A1.3). Codes not listed fall back to `failed`.
     */
    failedReason: {
      virus_detected: 'Malware detected — file removed',
      upload_missing: 'Upload never finished',
      transcode_failed: "We couldn't process this media",
    } as Record<string, string>,

    /** Retry affordance shown next to a failure when the surface supports one. */
    retry: 'Try again',

    /** Next to a failed chip whose bytes are still published (A3 row 10). */
    downloadOriginal: 'Download original',

    /** Remove affordance. */
    remove: 'Remove file',

    /** Accessible label for the thumbnail's no-preview icon. */
    fileIcon: 'File',

    /**
     * Learner-facing chip copy for an ACCEPTED answer file (file-handling plan
     * A1.3): once accepted the file is ours — processing never affects whether
     * the question is answered, and a post-acceptance failure is never shown as
     * failure or "retry" to a one-shot learner. Statuses render as
     * processing (type-specific) / received / ready.
     */
    learner: {
      /** Images and documents: the synchronous checks passed; the malware scan runs after. */
      checkingMalware: 'Checking for malware…',
      /** Video and audio: transcode runs for minutes. */
      processingMedia: 'Processing…',
      /** A failed FileRef, neutrally: the file was received and counted. */
      received: 'Received',
    },
  },

  /**
   * Avatar/image editor messages
   */
  avatar: {
    /** Drop zone text for avatar */
    dropOrClick: 'Drop or click',

    /** Full drop zone text */
    dropImageHere: 'Drop image here or click to browse',

    /** Remove image tooltip */
    removeTooltip: 'Remove and upload different image',

    /** Remove confirmation */
    removeConfirm: 'Remove?',

    /** Logo fallback text */
    logo: 'Logo',
  },

  /**
   * Search/autocomplete messages
   */
  search: {
    /** No results found */
    noResults: 'No results found',

    /** No items available */
    noItemsAvailable: 'No items available',

    /** Loading/searching */
    searching: 'Searching...',
  },

  /**
   * Subheader/layout messages
   */
  subheader: {
    /** Selection prompts */
    selectItemsFirst: 'Select items first',
    selectAtLeast2: 'Select at least 2 items',
    selectOnly1: 'Select only 1 item',

    /** Selection count */
    selected: 'selected',

    /** Revision count tooltip */
    revisionCount: (count: number) => `${count} revision${count !== 1 ? 's' : ''}`,
  },

  /**
   * Filter labels and navigation section headers
   */
  filters: {
    status: 'Status',
    sort: 'Sort',
    owner: 'Owner',
    anyone: 'Anyone',
    me: 'Me',
    /** Tri-state toggle labels */
    any: 'Any',
    yes: 'Yes',
    no: 'No',
    /** Section headers for FilterNavigation */
    attributes: 'Attributes',
    labels: 'Labels',
    filtersSection: 'Filters',
    /** Saved-filter section header (default; Analytics overrides to "Saved Contact Filters") */
    savedFilters: 'Saved Filters',
    /** Filter navigation actions */
    clear: 'Clear',
    noLabelsInCategory: 'No labels',
    dropLabelsToCreate: 'Drop labels here to create folder',
    /** Drag selection */
    labelsSelected: (count: number) => `${count} selected`,
    labelDragBadge: (count: number) => `${count} label${count === 1 ? '' : 's'}`,
  },

  /**
   * Save status messages
   */
  saveStatus: {
    saving: 'Saving...',
    saved: 'Saved',
    saveFailed: 'Save failed',
  },

  /**
   * Accessibility labels
   */
  accessibility: {
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
  },

  /**
   * Analytics toolbar labels
   */
  analytics: {
    selected: (count: number) => `${count} selected`,
    /** Status labels for progress/sentiment cells */
    complete: 'Complete',
    onTrack: 'On track',
    behind: 'Behind',
    notStarted: 'Not started',
    completed: 'Completed',
  },

  /**
   * Dashboard labels
   */
  dashboard: {
    viewAll: 'View All',
    /** Chart labels */
    chartCompleted: 'Completed',
    chartViewed: 'Viewed',
    chartNotViewed: 'Not Viewed',
    /** Fallback chart titles */
    engagement: 'Engagement',
    activity: 'Activity',
    submissions: 'Submissions',
    /** Section titles */
    recentContacts: 'Recently Active',
    recentPackets: 'Recently Viewed',
    recentChecklists: 'Recently Submitted',
    /** Packet type labels */
    course: 'Course',
    requirement: 'Requirement',
    /** Sentiment fallback labels */
    satisfied: 'Satisfied',
    dissatisfied: 'Dissatisfied',
    neutral: 'Neutral',
    /** Submission count */
    submissionCount: (count: number) => `${count} submission${count !== 1 ? 's' : ''}`,
  },

  /**
   * Connectivity failure copy (#239, owner copy rule 2026-09-09).
   * A transport failure (no HTTP response) tells the user to check THEIR
   * connection — never "Something went wrong" (that reads as our fault).
   */
  connectivity: {
    unreachable: "Can't reach Ving. Check your internet connection, then retry.",
    /** #447: server answered but with an error (429/5xx) — the internet is fine. */
    serverNotResponding: "Ving isn't responding right now. Wait a moment, then retry.",
  },

  /**
   * Ving support (internal) contact refusals (#807 / vingapi#1093). The
   * server's own sentence is always the primary text where one is sent; these
   * are the client-side summaries for surfaces that only get counts (import
   * stream entries, bulk archive results).
   */
  internalContacts: {
    /** Shown to staff only: where the visibility setting lives. */
    staffAddendum:
      "Staff can turn on “Show Ving support contacts” in this organization's or network's People settings.",
    /** Import stats key "Refused (hidden support contact)" > 0. */
    importRefusedHidden: (count: number, scope: 'organization' | 'network' | 'organization or network') =>
      `${count} ${pluralize('row', count)} not imported: the address belongs to a Ving support contact hidden for this ${scope}.`,
    /** Contact import stats key "Archive refused (support contact)" > 0. */
    importArchiveRefused: (count: number) =>
      `${count} ${pluralize('row', count)}: archived status not changed for a Ving support contact; the rest of the row was imported.`,
  },

  /**
   * Network-admin settings forms. `noviSsoCheckFields` blames the person's input:
   * said only for a 400 with a LIST detail (the server named the fields), #1338;
   * src/__tests__/inputBlameMessages.guard.test.ts pins its one site.
   */
  settingsForms: {
    noviSsoCheckFields: 'Could not save NOVI SSO settings. Check the fields and try again.',
  },
  /**
   * Consume viewer labels
   */
  consume: {
    /** Access denied page */
    contentOwner: 'Content owner',
    accessDenied: 'Access Denied',
    noPermission: 'You do not have permission to view this content.',
    signInAs: (email: string) => `Sign in as: ${email}`,
    // Legacy vingclient's error dialog tied every auth CTA to resuming access
    // ("Log In to Continue", etc.) rather than the bare action name — keep
    // that phrasing here.
    logIn: 'Log In to Continue',
    signUp: 'Sign Up to Continue',
    logOut: 'Log Out to Continue',
    returnHome: 'Return Home',
    // Legacy vingclient's error dialog always carried this footer; restore it
    // verbatim — vingreact's AccessDenied had dropped it.
    supportContact: 'Questions? Contact ',
    supportEmail: 'support@vingapp.com',
    /** Access denied page — per-code titles (vingapi sends `message`, not `title`) */
    authRequiredTitle: 'Sign In to Continue',
    assignmentRequiredTitle: 'Not Assigned to You',
    wrongAccountMessage:
      "This content wasn't assigned to your account. Log out and sign in with the account it was sent to, or contact whoever shared it with you.",
    /**
     * Verify-method offer (vingapi's `offer` on an assignment_required 403).
     * The person followed a link addressed to one contact method and is signed
     * in on an account holding a different one — so this is NOT the
     * wrong-account dead end above: the content IS theirs, and proving they
     * own the named address is all that stands between them and it. The copy
     * names the address because vingapi only ever offers one the request
     * already supplied.
     */
    verifyOfferTitle: 'Confirm This Is You',
    verifyOfferIntro: (method: string) =>
      `This was sent to ${method}. Add it to your account and confirm the code we send there, and the content will open — no need to sign out.`,
    verifyOfferSend: 'Send Code',
    verifyOfferResend: 'Resend Code',
    verifyOfferCodeLabel: 'Verification code',
    verifyOfferCodeSent: (method: string) => `We sent a code to ${method}.`,
    verifyOfferSubmit: 'Confirm',
    verifyOfferSendFailed: 'We could not send a code to that address. Please try again.',
    verifyOfferVerifyFailed: 'That code did not match. Please check it and try again.',
    verifyOfferSuccess: 'Confirmed — opening your content.',
    /** The offer's alternative: it really is someone else's account. */
    verifyOfferOtherAccount: 'Not your address? Log out and sign in with the account it was sent to.',
    contentNotReadyTitle: 'Not Available Yet',
    previewNotAuthorizedTitle: 'Preview Not Available',
    previewAuthRequiredTitle: 'Sign In to Preview',
    /** Preview mode */
    previewModeNotice: 'Preview mode: activity and scores will not be tracked',
    /** Review mode — revisiting a closed (finalized) assignment; the mirror of preview */
    reviewModeNotice: 'Review mode: this assignment is already closed',
    /** View container */
    invalidViewLink: 'Invalid view link',
    unableToLoadContent: 'Unable to load content. The link may be invalid or expired.',
    /** Packet and checklist viewers (#1574, Q-506 card 1574, owner 10-14; checklist Q-569): answers unsaved for 15 s, whatever the cause.
     *  Replaces the "trouble saving ... reconnect" line; the quiet Try again (RegionTryAgain) follows it.
     *  Failure recovery (#989 Q-204): no reference id in the learner's text. The id still travels on
     *  the request (`traceparent`) and on the banner decision (`ConsumeSaveBanner.referenceId`). */
    notSavedYet: 'Not saved yet. Keep this page open.',
    /**
     * #989 S4u: the server holds answers for a component saved after this page loaded. They
     * came from another window or device, or from this page's own reload during a slow save
     * (#1308, Q-377 a), so the line names no cause. Owner Q-570 = a (2026-10-20), replacing
     * Q-209 b's wording. One string: the banner renders it verbatim.
     */
    saveChangedElsewhere:
      'These answers were changed after this page loaded. Reload to continue.',
    /**
     * #1029: the page's consume row changed (reassignment, or another person signed in on
     * this device) and the server refused the save (409 consume_key_mismatch). Owner Q-382 = a
     * (2026-10-07). One string: the banner renders it verbatim, never the server's message.
     */
    saveKeyMismatch: 'This page is out of date. Reload to continue. Answers entered here will not be kept.',
    /** #989 S4u: the visible label of the "changed elsewhere" banner's Reload control. */
    saveReload: 'Reload',
    /**
     * The course viewer's save banner when a save is refused because sign-in is needed
     * (vingreact#1578; owner Q-642 all-accept 10-23 and Q-506 10-14). The checklist viewer shows the
     * same two strings (vingreact#1990; owner Q-679, 2026-11-02). The server decides THAT sign-in
     * is needed; its own words are never shown here. `logIn` stays "Log In to Continue" for AccessDenied.
     */
    saveSignInMessage: 'Sign in to save your progress.',
    saveSignIn: 'Sign In',
    /** Offline save banner — warning severity, keep-tab-open guidance (W1/W3). */
    saveOfflineMessage:
      "Keep this page open — we'll save your progress as soon as you're back online.",
    /** Checklist submit catch when the request never got a response (W1/W3). */
    submitUnreachable:
      "Couldn't reach Ving — check your connection and try again.",
    /** View GET while offline — inline copy instead of the invalid-link lie (W1/W4). */
    loadOffline:
      "You appear to be offline. We'll load this as soon as you're back online.",
    /** PDF transport error after retry exhaustion (W1/W4). */
    pdfUnreachable:
      "Couldn't download this document — check your connection and retry.",
    /** Signed file URL rejected twice or refresh failed (#541). The app cannot tell an
     *  expired signature from any other 403, so the copy names no cause. */
    fileUrlExpired:
      'This file could not be loaded. Reload the page to try again.',
    /** The browser cannot decode the file; reloading cannot help (#1024, Q-407). */
    videoCannotPlay: "This video can't play on this device.",
    audioCannotPlay: "This audio can't play on this device.",
    /** Reload button label for the expired-URL affordance (#541). */
    fileUrlReload: 'Reload',
    // Fallback for a TERMINAL save rejection (403/409/422/400) when the server
    // sent no message of its own. A terminal rejection never says "reconnect".
    saveRejectedMessage: 'This change could not be saved.',
    contentTroubleMessage: (referenceId: string) =>
      `We're having trouble opening this content. Please try again later and reference ${referenceId} if you need help.`,
    /** Completion banner (#1110: the learner view's banner at the end of a course; no dialog). */
    complete: 'Complete!',
    sessionComplete: 'Session Complete',
    almostDone: 'Almost Done',
    goToSkipped: 'Go to Skipped',
    goToDashboard: 'Go to Dashboard',
    completeMessage: 'You have finished all of the content.',
    /** Not signed in, and the server has the completion with nothing queued, held or stranded. */
    completeRecordedMessage: 'You have finished all of the content, and it is recorded. You can close this page.',
    skippedMessage: "You've reached the end, but some content was skipped. Would you like to go back to the first skipped item?",
    /** Checklist Submitted banner (#1110c: under the checklist's header; no dialog, no Done). */
    checklistSubmittedTitle: 'Submitted!',
    /** Signed in (the control is Go to Dashboard). */
    checklistSubmittedMessage: 'Your responses have been submitted.',
    /** Not signed in: the server returned `submittedDate`, so the page can be closed. */
    checklistSubmittedClosableMessage: 'Your responses have been submitted. You can close this page.',
    /** Time formatting */
    aboutHours: (hours: number) => `about ${hours} ${hours === 1 ? 'hour' : 'hours'}`,
    aboutMinutes: (minutes: number) => `about ${minutes} ${pluralize('minute', minutes)}`,
    /**
     * "There is/are {value} remaining." with subject–verb agreement: singular
     * ("is") only for exactly one hour; plural ("are") for half-hours, multiple
     * hours, and minutes (matches legacy `dialog.view.status.component.ts:60`).
     */
    remainingClause: (value: string, singular: boolean) =>
      `There ${singular ? 'is' : 'are'} ${value} remaining.`,
    microburstMessage: (interval: number, remainingClause: string, minutes: number) =>
      `You're finished for ${interval} ${interval === 1 ? 'day' : 'days'}. ${remainingClause} We'll remind you when it's time to complete the next ${minutes}-minute session. See you next time!`,
    /** Question display */
    required: 'Required',
    questionImage: 'Question',
    enterAnswer: 'Enter your answer...',
    fileUploaded: 'File uploaded',
    uploadingEllipsis: 'Uploading...',
    uploadFile: 'Upload File',
    /** File upload */
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    document: 'Document',
    uploadFailed: 'Failed to upload file',
    /** Checklist submit refused: the server listed required questions still unanswered (plan R3). */
    checklistRequiredIncomplete: 'Please answer the highlighted required questions before submitting.',
    /** Inline marker under a question the server listed as incomplete. */
    questionIncomplete: 'This question needs an answer',
    noFilesSelected: 'No files selected, please try again.',
    onlyOneFile: 'You can only choose one file to be uploaded.',
    filesNotUploadedPreview: 'Files are not uploaded while previewing.',
    uploadingDoNotClose: 'Uploading file do not close...',
    fileAttached: 'File attached',
    /** Suffix appended to an attached file's name while it is still processing. */
    fileProcessingSuffix: '(processing)',
    typeAttached: (type: string) => `${type.charAt(0).toUpperCase() + type.slice(1)} attached`,
    viewFile: 'View file',
    removeFile: 'Remove file',
    attachImage: 'Attach Image',
    attachVideo: 'Attach Video',
    attachAudio: 'Attach Audio',
    attachDocument: 'Attach Document',
    attachFile: 'Attach File',
    allowed: (types: string) => `Allowed: ${types}`,
    fileMustBe: (types: string) => `File must be: ${types}`,
  },
} as const;

export type FormMessageCategory = keyof typeof formMessages;
